import { PARQ_QUESTIONS } from '../constants/parqQuestions';
import { mapAnamnesisToPublicParqForm } from '../pages/ParQ/ParQRegister/publicParq.utils';
import type { Anamnesis } from '../types/anamnesis';
import type { AnamneseFormData, ParQFormData } from '../types/registration';

const trimOrNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed || null;
};

const enumOrNull = (value: string): string | null => value || null;

const sanitizeParqForm = (parq: ParQFormData): ParQFormData => ({
  ...parq,
  heartConditionOther: parq.heartCondition === true ? parq.heartConditionOther : '',
});

export const mapStudentRegisterAnamnesisToRecord = (
  anamnese: AnamneseFormData,
): Partial<Anamnesis> => ({
  online_course: enumOrNull(anamnese.onlineCourse),
  other_course: trimOrNull(anamnese.otherCourse),
  previous_online_consultancy: trimOrNull(anamnese.previousOnlineConsultancy),
  course_feedback: trimOrNull(anamnese.courseFeedback),
  training_level: enumOrNull(anamnese.trainingLevel),
  training_level_other: anamnese.trainingLevel === 'other'
    ? trimOrNull(anamnese.trainingLevelOther)
    : null,
  profession: trimOrNull(anamnese.profession),
  diastasis_treatment: anamnese.abdominalDiastasis === true
    ? trimOrNull(anamnese.diastasisTreatment)
    : null,
  training_location: enumOrNull(anamnese.trainingLocation),
  available_equipment: trimOrNull(anamnese.availableEquipment),
  full_gym_name: trimOrNull(anamnese.fullGymName),
  weekly_availability: trimOrNull(anamnese.weeklyAvailability),
  available_training_time: trimOrNull(anamnese.availableTrainingTime),
  had_injury: anamnese.hadInjury,
  injury_description: trimOrNull(anamnese.injuryDescription),
  has_pain: anamnese.hasPain,
  pain_location: trimOrNull(anamnese.painLocation),
  pain_limits_training: anamnese.painLimitsTraining,
  limited_exercises: trimOrNull(anamnese.limitedExercises),
  currently_exercises: anamnese.currentlyExercises,
  exercise_modalities: trimOrNull(anamnese.exerciseModalities),
  time_without_exercise: trimOrNull(anamnese.timeWithoutExercise),
  exercise_history: trimOrNull(anamnese.exerciseHistory),
  enjoys_training: anamnese.enjoysTraining,
  used_to_effort: anamnese.usedToEffort,
  has_nutritional_support: anamnese.hasNutritionalSupport,
  diet_detail: trimOrNull(anamnese.dietDetail),
  takes_medication: anamnese.takesMedication,
  medications: trimOrNull(anamnese.medications),
  had_surgery: anamnese.hadSurgery,
  surgeries: trimOrNull(anamnese.surgeries),
  has_chronic_disease: anamnese.hasChronicDisease,
  chronic_diseases: trimOrNull(anamnese.chronicDiseases),
  has_family_history: anamnese.hasFamilyHistory,
  family_history_description: trimOrNull(anamnese.familyHistoryDescription),
  has_children: anamnese.hasChildren,
  time_since_delivery: trimOrNull(anamnese.timeSinceDelivery),
  abdominal_diastasis: anamnese.abdominalDiastasis,
  main_goal: enumOrNull(anamnese.mainGoal),
  goal_details: trimOrNull(anamnese.goalDetails),
  additional_info: trimOrNull(anamnese.additionalInfo),
  referral_source: enumOrNull(anamnese.referralSource),
  motivation: trimOrNull(anamnese.motivation),
});

export const mapStudentRegisterParqToAnamnesisRecord = (
  parq: ParQFormData,
): Partial<Anamnesis> => {
  const sanitizedParq = sanitizeParqForm(parq);
  const record: Partial<Anamnesis> = {};

  PARQ_QUESTIONS.forEach((question) => {
    record[question.dbColumn] = sanitizedParq[question.field];
  });

  record.parq_heart_condition_other = sanitizedParq.heartCondition === true
    ? trimOrNull(sanitizedParq.heartConditionOther)
    : null;

  const responsibility = trimOrNull(sanitizedParq.responsibility);
  if (responsibility) {
    record.parq_responsibility = responsibility;
  }

  return record;
};

export const mapAnamnesisToStudentRegisterAnamneseForm = (
  anamnesis: Anamnesis,
  defaults: Pick<AnamneseFormData, 'heightCm' | 'weightKg' | 'instagram'>,
): AnamneseFormData => {
  const injParts = (anamnesis.injury_description || '').split('\nDores: ');
  const injuryDesc = injParts[0] || '';
  const painLoc = injParts.length > 1 ? injParts[1] : '';

  const knownTrainingLevels = new Set([
    'beginner',
    'intermediate',
    'intermediate_advanced',
    'advanced',
    'very_advanced',
    'other',
  ]);
  const rawTrainingLevel = anamnesis.training_level || '';
  const trainingLevelIsKnown = knownTrainingLevels.has(rawTrainingLevel);
  const trainingLevel = (
    trainingLevelIsKnown
      ? rawTrainingLevel
      : ((anamnesis.training_level_other || rawTrainingLevel) ? 'other' : '')
  ) as AnamneseFormData['trainingLevel'];
  const trainingLevelOther = anamnesis.training_level_other
    || (!trainingLevelIsKnown ? rawTrainingLevel : '')
    || '';

  return {
    ...defaults,
    profession: anamnesis.profession || '',
    onlineCourse: (anamnesis.online_course as AnamneseFormData['onlineCourse']) || '',
    otherCourse: anamnesis.other_course || '',
    previousOnlineConsultancy: anamnesis.previous_online_consultancy || '',
    courseFeedback: anamnesis.course_feedback || '',
    trainingLevel,
    trainingLevelOther,
    trainingLocation: (anamnesis.training_location as AnamneseFormData['trainingLocation']) || '',
    availableEquipment: anamnesis.available_equipment || '',
    fullGymName: anamnesis.full_gym_name || '',
    weeklyAvailability: anamnesis.weekly_availability || '',
    availableTrainingTime: anamnesis.available_training_time || '',
    hadInjury: anamnesis.had_injury ?? null,
    injuryDescription: injuryDesc,
    hasPain: anamnesis.has_pain ?? null,
    painLocation: anamnesis.pain_location || painLoc,
    painLimitsTraining: anamnesis.pain_limits_training ?? null,
    limitedExercises: anamnesis.limited_exercises || '',
    currentlyExercises: anamnesis.currently_exercises ?? null,
    exerciseModalities: anamnesis.exercise_modalities || '',
    timeWithoutExercise: anamnesis.time_without_exercise || '',
    exerciseHistory: anamnesis.exercise_history || '',
    enjoysTraining: anamnesis.enjoys_training ?? null,
    usedToEffort: anamnesis.used_to_effort ?? null,
    hasNutritionalSupport: anamnesis.has_nutritional_support ?? null,
    dietDetail: anamnesis.diet_detail || '',
    takesMedication: anamnesis.takes_medication ?? null,
    medications: anamnesis.medications || '',
    hadSurgery: anamnesis.had_surgery ?? null,
    surgeries: anamnesis.surgeries || '',
    hasChronicDisease: anamnesis.has_chronic_disease ?? null,
    chronicDiseases: anamnesis.chronic_diseases || '',
    hasFamilyHistory: anamnesis.has_family_history ?? null,
    familyHistoryDescription: anamnesis.family_history_description || '',
    hasChildren: anamnesis.has_children ?? null,
    timeSinceDelivery: anamnesis.time_since_delivery || '',
    abdominalDiastasis: anamnesis.abdominal_diastasis ?? null,
    diastasisTreatment: anamnesis.diastasis_treatment || '',
    mainGoal: (anamnesis.main_goal as AnamneseFormData['mainGoal']) || '',
    goalDetails: anamnesis.goal_details || '',
    additionalInfo: anamnesis.additional_info || '',
    referralSource: (anamnesis.referral_source as AnamneseFormData['referralSource']) || '',
    motivation: anamnesis.motivation || '',
  };
};

export const mapAnamnesisToStudentRegisterForms = (
  anamnesis: Anamnesis,
  defaults: Pick<AnamneseFormData, 'heightCm' | 'weightKg' | 'instagram'>,
): { anamnese: AnamneseFormData; parq: ParQFormData; aiSummary: string | null } => ({
  anamnese: mapAnamnesisToStudentRegisterAnamneseForm(anamnesis, defaults),
  parq: mapAnamnesisToPublicParqForm(anamnesis),
  aiSummary: anamnesis.ai_summary ?? null,
});
